import { apiJson } from '../api';
import type { AuthResponse, User } from '../types';

export async function login(usernameOrEmail: string, password: string): Promise<AuthResponse> {
  return apiJson<AuthResponse>('/api/auth/login', {
    method: 'POST',
    json: { usernameOrEmail, password },
    public: true,
  });
}

export async function signup(username: string | undefined, email: string, password: string): Promise<AuthResponse> {
  return apiJson<AuthResponse>('/api/auth/signup', {
    method: 'POST',
    json: { username, email, password },
    public: true,
  });
}

export type GoogleExchangeResponse = AuthResponse & { return_to: string; daily_brief: boolean };

/** Trade the single-use code from the Google sign-in callback for a session. */
export async function exchangeGoogleCode(code: string): Promise<GoogleExchangeResponse> {
  return apiJson<GoogleExchangeResponse>('/api/auth/google/exchange', {
    method: 'POST',
    json: { code },
    public: true,
  });
}

/** Backend endpoint that starts Google sign-in; the browser navigates to it on the host the sign-in finishes on
 *  (the reader's, or evals.tldr.fm's with site 'evals'). */
export function googleSignInUrl(returnTo: string, dailyBrief: boolean, site: 'reader' | 'evals' = 'reader'): string {
  const params = new URLSearchParams({ return_to: returnTo, daily_brief: String(dailyBrief) });
  if (site !== 'reader') params.set('site', site);
  return '/api/auth/google/authorize?' + params;
}

export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  return apiJson<{ message: string }>('/api/auth/request-reset', {
    method: 'POST',
    json: { email },
    public: true,
  });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await apiJson('/api/auth/reset-password', {
    method: 'POST',
    json: { token, new_password: newPassword },
    public: true,
  });
}

/** The session's own user, even while the tab views as another reader (it becomes the stored identity). */
export async function fetchCurrentUser(): Promise<User> {
  const data = await apiJson<{ user: User }>('/api/me', { asSelf: true });
  return data.user;
}

/** Whom the tab's requests answer as: the viewed reader while viewing as one (`utils/viewAs`), else the session's user. */
export async function fetchAnsweringUser(): Promise<User> {
  const data = await apiJson<{ user: User }>('/api/me');
  return data.user;
}
